import {IEnvironment} from '../../../../environments/ienvironment';
import {MenuContext, MenuExternal, MenuGroup, MenuItem, MenuLink} from './menu-item';

/**
 * Predicate that decides whether the current user holds a given privilege.
 * Injected so the builder stays free of Angular dependencies and is unit-testable.
 */
export type PrivilegeChecker = (privilege: string) => boolean;

/**
 * Filters a menu config down to the items the current user is allowed to see.
 *
 * Each item is dropped when:
 *   - its `hidden(ctx)` predicate returns true, or
 *   - it is a link whose route privilege (and any `extraPrivileges`) all fail the privilege check, or
 *   - it is an external link whose `env:` href resolves to an empty string, or
 *   - it is a group whose children all get filtered out.
 *
 * Dividers that end up leading, trailing, or adjacent to another divider after filtering
 * are also stripped so the rendered list never has dangling separators.
 *
 * @param config       the static menu definition (e.g. `GO_MENU` or `GETPAID_MENU`)
 * @param ctx          runtime flags (feature toggles, env) the predicates check against
 * @param hasPrivilege adapter calling into the auth service
 * @returns a clean tree the template can iterate without further guards
 */
export function buildMenu(config: MenuItem[], ctx: MenuContext, hasPrivilege: PrivilegeChecker): MenuItem[]
{
    const processed = config
        .map(item => processItem(item, ctx, hasPrivilege))
        .filter((item): item is MenuItem => item !== null);
    return tidyDividers(processed);
}

/**
 * Throws if any group in the config nests another group as a child.
 *
 * The renderer enforces a maximum visual depth of 2 (group → leaf) because
 * deeper nesting in the sidebar starts to look broken; the assertion catches
 * config mistakes at construction time rather than letting them ship.
 */
export function assertMaxDepth(items: MenuItem[], depth: number = 0): void
{
    for (const item of items)
    {
        if (item.kind === 'group')
        {
            if (depth >= 1)
            {
                throw new Error(`Menu config violates max-depth-2 rule at group "${item.label}".`);
            }
            assertMaxDepth(item.children, depth + 1);
        }
    }
}

/**
 * Returns every group's `collapseKey` in the tree.
 *
 * Used by the layout component to seed the collapsed-state map and to drive
 * mutual-exclusion (opening one group collapses every other key in this list).
 */
export function collectGroupKeys(items: MenuItem[]): string[]
{
    const keys: string[] = [];
    for (const item of items)
    {
        if (item.kind === 'group')
        {
            keys.push(item.collapseKey, ...collectGroupKeys(item.children));
        }
    }
    return keys;
}

/**
 * Resolves an `env:<field>` token to the corresponding string field on the environment.
 *
 * Plain hrefs (e.g. `/public/faqs`) pass through unchanged. Tokens that point at a missing
 * or non-string field resolve to `''` so `buildMenu` can drop the item rather than render
 * a broken link.
 */
export function resolveEnvHref(href: string | undefined, env: IEnvironment): string | undefined
{
    if (href === undefined)
    {
        return undefined;
    }
    if (href.startsWith('env:'))
    {
        const key = href.slice('env:'.length) as keyof IEnvironment;
        const value = env[key];
        return typeof value === 'string' ? value : '';
    }
    return href;
}

/**
 * Filter logic for a single item — returns the item (possibly with resolved fields)
 * or `null` if it should be hidden. Dividers always survive this step; `tidyDividers`
 * cleans up any that became orphaned after siblings were filtered.
 */
function processItem(item: MenuItem, ctx: MenuContext, hasPrivilege: PrivilegeChecker): MenuItem | null
{
    if (item.kind === 'divider')
    {
        return item;
    }
    if (item.hidden?.(ctx))
    {
        return null;
    }
    if (item.kind === 'link')
    {
        const link: MenuLink = item;
        const privileges = [link.route.privilege, ...(link.extraPrivileges ?? [])];
        return privileges.some(p => hasPrivilege(p)) ? link : null;
    }
    if (item.kind === 'group')
    {
        const group: MenuGroup = item;
        const children = buildMenu(group.children, ctx, hasPrivilege);
        return children.length ? {...group, children} : null;
    }
    const ext: MenuExternal = item;
    const resolvedHref = resolveEnvHref(ext.href, ctx.env);
    if (ext.href !== undefined && !ext.onClick && !resolvedHref)
    {
        return null;
    }
    return {...ext, href: resolvedHref};
}

/**
 * Drops leading, trailing, and consecutive dividers from a sibling list so a
 * filtered group never renders with dangling or doubled-up separators.
 */
function tidyDividers(items: MenuItem[]): MenuItem[]
{
    const cleaned: MenuItem[] = [];
    for (const item of items)
    {
        if (item.kind === 'divider')
        {
            // skip leading or back-to-back dividers
            if (cleaned.length === 0 || cleaned[cleaned.length - 1].kind === 'divider')
            {
                continue;
            }
        }
        cleaned.push(item);
    }
    // drop trailing dividers
    while (cleaned.length && cleaned[cleaned.length - 1].kind === 'divider')
    {
        cleaned.pop();
    }
    return cleaned;
}
